import { useRef, type CSSProperties } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  Baby,
  BatteryLow,
  CaretLeft,
  CaretRight,
  ChatsCircle,
  CloudRain,
  Drop,
  FirstAid,
  FlowerLotus,
  GenderIntersex,
  HandPalm,
  Handshake,
  Heart,
  HeartBreak,
  Lifebuoy,
  Sparkle,
  Users,
  Wind,
  type Icon,
} from "@phosphor-icons/react";
import { availableTopicsQuery } from "@/api-queries/available-topics";
import { useAutoDrift } from "@/hooks/useAutoDrift";
import type { MatchingProblemTag } from "@/types/matching";

// One icon per problem tag. UI-only, stays local per CLAUDE.md. Unknown tags
// (should never happen — the API filters via isProblemTag) fall back to Lifebuoy.
const TOPIC_ICONS: Record<MatchingProblemTag, Icon> = {
  anxiety: Wind,
  depression: CloudRain,
  burnout: BatteryLow,
  trauma: FirstAid,
  relationships: Heart,
  family: Users,
  self_esteem: Sparkle,
  grief: Drop,
  parenting: Baby,
  addiction: HandPalm,
  sexual_problems: FlowerLotus,
  gender_identity: GenderIntersex,
  infidelity: HeartBreak,
  friendships: Handshake,
  communication: ChatsCircle,
  uncertainty: Lifebuoy,
};

// One tint per theme, and the tint *is* the card — no paper mix, no per-card
// ink. The register is deliberately earthy and low-chroma so the deck sits
// inside the olive hero rather than shouting over it; the terracotta CTA above
// stays the only fully saturated thing on the screen. Lightness is held in a
// narrow band (L53–80) so the shared near-black ink clears contrast on every
// card, and neighbouring hues stay far enough apart to never read as a pair.
const TOPIC_ACCENTS: Record<MatchingProblemTag, string> = {
  anxiety: "oklch(70% 0.048 245)", // dusty blue
  depression: "oklch(67% 0.045 275)", // muted periwinkle
  burnout: "oklch(72% 0.09 66)", // ochre
  trauma: "oklch(66% 0.05 350)", // muted mauve
  relationships: "oklch(58% 0.125 42)", // rust
  family: "oklch(79% 0.055 130)", // sage
  self_esteem: "oklch(78% 0.125 82)", // mustard
  grief: "oklch(73% 0.03 45)", // rose taupe
  parenting: "oklch(80% 0.06 62)", // sand
  addiction: "oklch(53% 0.1 30)", // brick
  sexual_problems: "oklch(64% 0.07 10)", // dusty rose
  gender_identity: "oklch(72% 0.05 300)", // lilac
  infidelity: "oklch(60% 0.06 20)", // clay
  friendships: "oklch(76% 0.06 160)", // mint
  communication: "oklch(68% 0.05 210)", // slate teal
  uncertainty: "oklch(70% 0.05 115)", // moss
};

const FALLBACK_ACCENT = "oklch(73% 0.03 45)";

// Above this many themes the row can't sit still on a desktop screen, so the
// arrows earn their place. At or below it every card is visible and they'd be
// dead controls.
const ARROWS_FROM = 6;

function scrollByCards(el: HTMLDivElement | null, direction: 1 | -1) {
  if (!el) return;
  const reduce =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  // ~80% of the visible width so a couple of cards stay in view for context.
  el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: reduce ? "auto" : "smooth" });
}

export function HeroTopicsCarousel() {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { data } = useQuery(availableTopicsQuery);
  const topics = data?.topics ?? [];
  // Most visitors are on a phone, where the row has no arrows and the cut-off
  // card alone is easy to miss, so the deck drifts until it is touched.
  const stopDrift = useAutoDrift(scrollerRef, topics.length > 0);

  // No themes yet (no therapist has filled a matching profile) — the hero's own
  // CTA already covers the matching flow, so all that's missing is the way to
  // the unfiltered list.
  if (topics.length === 0) {
    return (
      <div className="animate-hero-rise mt-8" style={{ animationDelay: "400ms" }}>
        <Link
          to="/therapists"
          className="inline-flex items-center gap-2 text-sm font-semibold text-white underline decoration-white/40 underline-offset-4 transition-colors hover:decoration-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
        >
          {t("home.hero.ctaPrimary")}
          <ArrowRight className="size-4" weight="bold" />
        </Link>
      </div>
    );
  }

  const showArrows = topics.length >= ARROWS_FROM;

  return (
    <div className="mt-12">
      {/* Header row: what the deck is for, and — only when it overflows — the
          way through it. */}
      <div
        className="animate-hero-rise flex items-end justify-between gap-4"
        style={{ animationDelay: "380ms" }}
      >
        <p className="text-sm font-medium text-white/75 sm:text-base">
          {t("home.hero.topics.prompt")}
        </p>
        {showArrows && (
          <div className="hidden shrink-0 items-center gap-1.5 sm:flex">
            <button
              type="button"
              aria-label={t("home.hero.topics.prev")}
              onClick={() => {
                stopDrift();
                scrollByCards(scrollerRef.current, -1);
              }}
              className="flex size-9 items-center justify-center rounded-full border border-white/25 text-white/80 transition-colors hover:border-white/50 hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
            >
              <CaretLeft className="size-4" weight="bold" />
            </button>
            <button
              type="button"
              aria-label={t("home.hero.topics.next")}
              onClick={() => {
                stopDrift();
                scrollByCards(scrollerRef.current, 1);
              }}
              className="flex size-9 items-center justify-center rounded-full border border-white/25 text-white/80 transition-colors hover:border-white/50 hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
            >
              <CaretRight className="size-4" weight="bold" />
            </button>
          </div>
        )}
      </div>

      {/* The row. Still a snap scroller, because a phone can't hold five cards
          and the tag list is open-ended, but the cards sit flat, straight and
          evenly spaced, so it reads as a band rather than a track. The right
          margin cancels the centred container (50% - 50vw is exactly the gap
          between the container edge and the viewport edge, at any width) so an
          overflowing deck runs off the screen instead of being cut off mid-card
          against an arbitrary inner edge. Vertical padding leaves room for the
          hover lift. */}
      <div
        ref={scrollerRef}
        className="hero-topics-scroller -mx-5 mt-4 flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-smooth px-5 pb-6 pt-3 [scroll-padding-inline:1.25rem] sm:ml-0 sm:mr-[calc(50%-50vw)] sm:gap-4 sm:pl-0 sm:pr-8 sm:[scroll-padding-inline:0px]"
      >
        {topics.map((topic, i) => {
          const tag = topic.tag as MatchingProblemTag;
          const IconCmp = TOPIC_ICONS[tag] ?? Lifebuoy;
          const accent = TOPIC_ACCENTS[tag] ?? FALLBACK_ACCENT;
          // The snap slot itself never moves; the deal animation runs on the
          // inner wrapper. A scale on the snap area shifts its inline edge, the
          // scroller re-snaps mid-load, and Chrome then reports no LCP for
          // the whole page (Lighthouse NO_LCP, score 0).
          return (
            <div key={topic.tag} className="shrink-0 snap-start">
              <div className="hero-topic-slot" style={{ "--deal-index": i } as CSSProperties}>
                <Link
                  to="/therapists"
                  search={{ problemTag: topic.tag }}
                  style={{ "--accent": accent } as CSSProperties}
                  className="hero-topic-card flex h-[9.5rem] w-[9.5rem] flex-col rounded-xl p-4 text-left focus-visible:outline-none sm:h-[10.25rem] sm:w-[11.5rem] sm:p-5"
                >
                  <IconCmp className="hero-topic-icon size-7 sm:size-8" weight="bold" aria-hidden />
                  {/* The label slot always reserves two lines (2 x leading-1.15),
                      so a one-word theme leaves no dead space above it and every
                      card keeps the same interior rhythm. */}
                  <h3 className="font-heading mt-auto flex min-h-[2.3em] items-end text-[0.9375rem] font-bold leading-[1.15] tracking-[-0.01em] sm:text-base">
                    {topic.label}
                  </h3>
                  <span className="hero-topic-count mt-2 text-[0.75rem] font-medium tabular-nums">
                    {t("home.hero.topics.count", { count: topic.count })}
                  </span>
                </Link>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
